import clsx from "clsx";
import { FC, MutableRefObject, ReactElement } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import {
  MapLibrePolygonEditorActionsStyled,
  MapLibrePolygonEditorToolbarStyled,
} from "./maplibre-polygon-editor-toolbar.styled";
import { EDITOR_TOOL_GROUPS } from "./maplibre-polygon-editor.constants";
import {
  IMapLibreTraceControls,
  TPolygonEditorMode,
} from "./maplibre-polygon-editor.models";
import { isTracingMode } from "./maplibre-polygon-editor.utils";
import { MapLibrePolygonToolIcon } from "./maplibre-polygon-tool-icon";

interface IMapLibrePolygonEditorToolbarProps {
  mode: TPolygonEditorMode;
  onModeChange: (mode: TPolygonEditorMode) => void;
  /** Grip on the trace in progress, so the actions row can finish it or step a corner back. */
  traceControlsRef: MutableRefObject<IMapLibreTraceControls | undefined>;
}

/**
 * The polygon drawer's tool cluster — the MapLibre stand-in for the toolbar geoman added to the map, down
 * to its own icons.
 *
 * Every button is a toggle and only one can be down at a time, so clicking the active tool puts it away
 * and leaves no tool selected. That is also the state the editor opens in.
 *
 * Beside whichever button is down, geoman opened a row of text actions, and it is reproduced here: Finish
 * / Remove Last Vertex / Cancel while a shape is being traced, and a single Finish for the tools that
 * simply stay on until dismissed. The row is a sibling of the button, never a child of it — nested, every
 * action click bubbled to the button and toggled the tool off, so Remove Last Vertex also ended the trace.
 */
export const MapLibrePolygonEditorToolbar: FC<
  IMapLibrePolygonEditorToolbarProps
> = ({ mode, onModeChange, traceControlsRef }) => {
  const { t } = useTranslation();

  const handleSelect = useEvent((next: TPolygonEditorMode): void =>
    onModeChange(next === mode ? "none" : next),
  );

  const handleFinishMode = useEvent((): void => onModeChange("none"));
  const handleFinishTrace = useEvent((): void =>
    traceControlsRef.current?.finish(),
  );
  const handleRemoveLastVertex = useEvent((): void =>
    traceControlsRef.current?.removeLastVertex(),
  );
  const handleCancelTrace = useEvent((): void =>
    traceControlsRef.current?.cancel(),
  );

  const renderActions = (): ReactElement => {
    const actions = isTracingMode(mode)
      ? [
          { key: "area.mapActionFinish", onSelect: handleFinishTrace },
          {
            key: "area.mapActionRemoveLastVertex",
            onSelect: handleRemoveLastVertex,
          },
          { key: "area.mapActionCancel", onSelect: handleCancelTrace },
        ]
      : [{ key: "area.mapActionFinish", onSelect: handleFinishMode }];

    return (
      <MapLibrePolygonEditorActionsStyled>
        {actions.map(({ key, onSelect }) => (
          <button
            key={key}
            type="button"
            className="mapToolAction"
            onClick={onSelect}
          >
            {t(key)}
          </button>
        ))}
      </MapLibrePolygonEditorActionsStyled>
    );
  };

  return (
    <MapLibrePolygonEditorToolbarStyled>
      {EDITOR_TOOL_GROUPS.map((group, groupIndex) => (
        <div className="mapToolGroup" key={groupIndex}>
          {group.map(
            ({
              mode: toolMode,
              translationKey,
              iconPath,
              iconPathTransform,
            }) => {
              const label = t(translationKey);
              const isSelected = mode === toolMode;
              const select = (): void => handleSelect(toolMode);

              return (
                <div className="mapTool" key={toolMode}>
                  <button
                    type="button"
                    onClick={select}
                    aria-label={label}
                    aria-pressed={isSelected}
                    title={label}
                    className={clsx("mapToolBtn", { selected: isSelected })}
                  >
                    <MapLibrePolygonToolIcon
                      path={iconPath}
                      pathTransform={iconPathTransform}
                    />
                  </button>
                  {isSelected && renderActions()}
                </div>
              );
            },
          )}
        </div>
      ))}
    </MapLibrePolygonEditorToolbarStyled>
  );
};
